import React, { useState, useEffect } from 'react';
import './google-classroom-integration.css';

/**
 * Google Classroom LMS Integration Component
 * 
 * This component provides UI for:
 * 1. Connecting to Google Classroom
 * 2. Viewing connected courses
 * 3. Deploying Morpheus tutors to Google Classroom courses
 * 4. Managing institutional settings
 */
const GoogleClassroomIntegration = ({ user }) => {
  // States for Google Classroom integration
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('');
  const [courses, setCourses] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentResult, setDeploymentResult] = useState(null);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [institutionalMode, setInstitutionalMode] = useState(false);
  const [tokenName, setTokenName] = useState('Tokens');
  const [oauthInProgress, setOauthInProgress] = useState(false);

  // Check connection status on load
  useEffect(() => {
    checkConnectionStatus();
    
    // Listen for OAuth window callback
    const handleOAuthCallback = (event) => {
      if (event.data && event.data.type === 'googleClassroomConnected') {
        if (event.data.success) {
          setOauthInProgress(false);
          checkConnectionStatus();
        }
      }
    };
    
    window.addEventListener('message', handleOAuthCallback);
    
    return () => {
      window.removeEventListener('message', handleOAuthCallback);
    };
  }, []);

  // Check if connected to Google Classroom
  const checkConnectionStatus = async () => {
    setConnectionStatus('Checking connection...');
    
    try {
      const response = await fetch('/api/google_classroom/status');
      const data = await response.json();
      
      setIsConnected(data.connected);
      setConnectionStatus(data.connected ? 'Connected to Google Classroom' : 'Not connected to Google Classroom');
      
      if (data.connected) {
        fetchCourses();
        fetchModels();
      }
    } catch (error) {
      console.error('Error checking Google Classroom connection:', error);
      setConnectionStatus('Error checking connection');
      setIsConnected(false);
    }
  };

  // Connect to Google Classroom with API credentials
  const connectToGoogleClassroom = async () => {
    if (!clientId || !clientSecret || !refreshToken) {
      setConnectionStatus('Client ID, Client Secret, and Refresh Token are required');
      return;
    }
    
    setConnectionStatus('Connecting to Google Classroom...');
    
    try {
      const response = await fetch('/api/google_classroom/connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientId,
          clientSecret,
          refreshToken
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsConnected(true);
        setConnectionStatus('Successfully connected to Google Classroom');
        fetchCourses();
        fetchModels();
      } else {
        setConnectionStatus(`Failed to connect: ${data.error}`);
      }
    } catch (error) {
      console.error('Error connecting to Google Classroom:', error);
      setConnectionStatus('Error connecting to Google Classroom');
    }
  };

  // Start OAuth flow
  const startOAuthFlow = () => {
    if (!clientId || !clientSecret) {
      setConnectionStatus('Client ID and Client Secret are required for OAuth flow');
      return;
    }
    
    // Save client ID and secret to config first
    saveCredentials().then(() => {
      setOauthInProgress(true);
      
      // Open OAuth window
      const oauthWindow = window.open('/api/google_classroom/auth', 'Google Classroom Auth', 'width=800,height=600');
      
      // Check if window was blocked
      if (!oauthWindow || oauthWindow.closed || typeof oauthWindow.closed === 'undefined') {
        setConnectionStatus('Pop-up blocked. Please allow pop-ups for this site.');
        setOauthInProgress(false);
      } else {
        setConnectionStatus('OAuth flow started. Please complete the authorization in the pop-up window.');
      }
    }).catch(error => {
      console.error('Error saving credentials:', error);
      setConnectionStatus('Error saving credentials before OAuth flow');
      setOauthInProgress(false);
    });
  };

  // Save client ID and secret to config
  const saveCredentials = async () => {
    try {
      const response = await fetch('/api/google_classroom/connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientId,
          clientSecret,
          // We don't have refresh token yet, but we'll get it from OAuth
          refreshToken: ''
        }),
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Unknown error saving credentials');
      }
    } catch (error) {
      console.error('Error saving credentials:', error);
      throw error;
    }
  };

  // Fetch courses from Google Classroom
  const fetchCourses = async () => {
    try {
      const response = await fetch('/api/google_classroom/courses');
      const data = await response.json();
      
      if (data.success) {
        setCourses(data.courses);
      } else {
        console.error('Failed to fetch courses:', data.error);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  // Fetch available models
  const fetchModels = async () => {
    try {
      const response = await fetch('/api/models/list');
      const data = await response.json();
      
      if (data.success) {
        setModels(data.models);
      } else {
        console.error('Failed to fetch models:', data.error);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  // Deploy model to Google Classroom course
  const deployModel = async () => {
    if (!selectedCourseId) {
      setDeploymentResult({ success: false, message: 'Please select a course' });
      return;
    }
    
    if (!selectedModelId) {
      setDeploymentResult({ success: false, message: 'Please select a model' });
      return;
    }
    
    setIsDeploying(true);
    setDeploymentResult(null);
    
    try {
      const response = await fetch('/api/google_classroom/deploy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          courseId: selectedCourseId,
          modelId: selectedModelId,
          institutionalMode,
          tokenName,
        }),
      });
      
      const data = await response.json();
      
      setDeploymentResult({
        success: data.success,
        message: data.success 
          ? `Successfully deployed ${selectedModelId} to course ${selectedCourseId}` 
          : `Deployment failed: ${data.error}`,
      });
    } catch (error) {
      console.error('Error deploying model:', error);
      setDeploymentResult({
        success: false,
        message: 'Error deploying model',
      });
    } finally {
      setIsDeploying(false);
    }
  };

  return (
    <div className="google-classroom-integration">
      <h2>Google Classroom Integration</h2>
      
      <div className="connection-status">
        <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? 'Connected' : 'Disconnected'}
        </div>
        <p>{connectionStatus}</p>
      </div>
      
      {!isConnected && (
        <div className="connection-form">
          <h3>Connect to Google Classroom</h3>
          
          <div className="connection-methods">
            <div className="api-key-method">
              <h4>OAuth 2.0 Authentication</h4>
              <div className="form-group">
                <label htmlFor="clientId">Client ID:</label>
                <input
                  type="text"
                  id="clientId"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="Enter your Google API Client ID"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="clientSecret">Client Secret:</label>
                <input
                  type="password"
                  id="clientSecret"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder="Enter your Google API Client Secret"
                />
              </div>
              
              <div className="oauth-buttons">
                <button 
                  className="connect-button primary-button"
                  onClick={startOAuthFlow}
                  disabled={!clientId || !clientSecret || oauthInProgress}
                >
                  {oauthInProgress ? 'OAuth Flow In Progress...' : 'Connect with OAuth'}
                </button>
              </div>
              
              <div className="form-divider">
                <span>Or connect with credentials directly</span>
              </div>
              
              <div className="form-group">
                <label htmlFor="refreshToken">Refresh Token:</label>
                <input
                  type="password"
                  id="refreshToken"
                  value={refreshToken}
                  onChange={(e) => setRefreshToken(e.target.value)}
                  placeholder="Enter your Google API Refresh Token"
                />
              </div>
              
              <button 
                className="connect-button"
                onClick={connectToGoogleClassroom}
                disabled={!clientId || !clientSecret || !refreshToken}
              >
                Connect with API Credentials
              </button>
            </div>
            
            <div className="google-classroom-note">
              <h4>Note About Google Classroom API</h4>
              <p>
                To use this integration, you need to create a Google Cloud Platform project
                and enable the Google Classroom API.
              </p>
              <p>
                Follow these steps:
              </p>
              <ol className="setup-steps">
                <li>Go to the <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer">Google Cloud Console</a></li>
                <li>Create a new project</li>
                <li>Enable the Google Classroom API</li>
                <li>Create OAuth 2.0 credentials (Web application type)</li>
                <li>Set the redirect URI to: <code>{window.location.origin}/api/google_classroom/callback</code></li>
              </ol>
              <p>
                <strong>Important:</strong> Your application must be verified by Google for production use.
                During development, you can use it with test accounts that you explicitly approve.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {isConnected && (
        <div className="integration-panel">
          <div className="courses-section">
            <h3>Your Google Classroom Courses</h3>
            
            {courses.length === 0 ? (
              <p>No courses found. Create a course in Google Classroom first.</p>
            ) : (
              <div className="course-list">
                <select
                  value={selectedCourseId}
                  onChange={(e) => setSelectedCourseId(e.target.value)}
                >
                  <option value="">Select a course</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          <div className="models-section">
            <h3>Available Models</h3>
            
            {models.length === 0 ? (
              <p>No models available. Train or import a model first.</p>
            ) : (
              <div className="model-list">
                <select
                  value={selectedModelId}
                  onChange={(e) => setSelectedModelId(e.target.value)}
                >
                  <option value="">Select a model</option>
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} ({model.specialty})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          <div className="institutional-settings">
            <h3>Institutional Settings</h3>
            
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={institutionalMode}
                  onChange={(e) => setInstitutionalMode(e.target.checked)}
                />
                Enable Institutional Mode
              </label>
            </div>
            
            {institutionalMode && (
              <div className="form-group">
                <label htmlFor="tokenName">Token Display Name:</label>
                <input
                  type="text"
                  id="tokenName"
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  placeholder="Points, Stars, etc."
                />
                <p className="token-info">
                  This name will be used for tokens in Google Classroom announcements and comments.
                </p>
              </div>
            )}
          </div>
          
          <div className="deployment-section">
            <button
              className="deploy-button"
              onClick={deployModel}
              disabled={!selectedCourseId || !selectedModelId || isDeploying}
            >
              {isDeploying ? 'Deploying...' : 'Deploy to Google Classroom'}
            </button>
            
            {deploymentResult && (
              <div className={`deployment-result ${deploymentResult.success ? 'success' : 'error'}`}>
                {deploymentResult.message}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleClassroomIntegration;